import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../config/URL";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import GlobalDelete from "../../components/common/GlobalDelete";
import ReferalFeesAdd from "./ReferalFeesAdd";
import ReferalFeesEdit from "./ReferalFeesEdit";
import fetchAllCentersWithIds from "../List/CenterList";
import { toast } from "react-toastify";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";

const ReferalFees = ({ selectedCenter }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [centerId, setCenterId] = useState("");
  const [isClearFilterClicked, setIsClearFilterClicked] = useState(false);
  const [centerData, setCenterData] = useState([]);
  const centerLocalId = localStorage.getItem("selectedCenterId");
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
  });

  const storedScreens = JSON.parse(localStorage.getItem("screens") || {});
  const canIndex = Boolean(storedScreens?.referralFeesIndex);
  const canRead = Boolean(storedScreens?.referralFeesRead);
  const canCreate = Boolean(storedScreens?.referralFeesCreate);
  const canUpdate = Boolean(storedScreens?.referralFeesUpdate);
  const canDelete = Boolean(storedScreens?.referralFeesDelete);

  const showActionColumn = canUpdate || canDelete;

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) =>
          row.original.status === "ACTIVE" ? (
            <span className="badge badges-Green fw-light">Active</span>
          ) : (
            <span className="badge badges-Red fw-light">Inactive</span>
          ),
      },
      { accessorKey: "center", header: "Centre Name" },
      {
        accessorKey: "effectiveDate",
        header: "Effective Date",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      { accessorKey: "referralFee", header: "Referral Fee" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created Date",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions",
      header: "",
      size: 20,
      enableSorting: false,
      Cell: ({ cell }) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
            setSelectedId(cell.row.original.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    };

    return [baseColumns[0], actionColumn, ...baseColumns.slice(1)];
  }, [showActionColumn]);

  /* -------------------- API -------------------- */
  const fetchReferalData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      filters.centerId = selectedCenter;
      // Loop through other filters and add key-value pairs if they have a value
      for (let key in filters) {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      }
      let response;
      if (selectedCenter === 0 || selectedCenter === "0") {
        response = await api.get(`/getReferralFeeByCenterId`);
      } else {
        response = await api.get(
          `/getReferralFeeByCenterId?${queryParams.toString()}`
        );
      }

      setData(response.data);
    } catch (error) {
      toast.error(`Error Fetching Data: ${error.message}`);
    } finally {
      setLoading(false);
      setIsClearFilterClicked(false);
    }
  };

  const fetchCenterData = async () => {
    try {
      const centerData = await fetchAllCentersWithIds();
      if (centerLocalId !== null && centerLocalId !== "undefined") {
        setFilters((prevFilters) => ({
          ...prevFilters,
          centerId: centerLocalId,
        }));
      } else if (centerData !== null && centerData.length > 0) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          centerId: centerData[0].id,
        }));
      }
      setCenterData(centerData);
    } catch (error) {
      toast.error(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchCenterData(); // Fetch center data and subjects

      // Check if local storage has center ID
      if (centerLocalId && centerLocalId !== "undefined") {
        setFilters((prevFilters) => ({
          ...prevFilters,
          centerId: centerLocalId,
        }));
      } else if (centerData && centerData.length > 0) {
        // Use the first center's ID as the default if no center is in local storage
        setFilters((prevFilters) => ({
          ...prevFilters,
          centerId: centerData[0].id,
        }));
      }
    };
    fetchData();
  }, [selectedCenter]);

  useEffect(() => {
    fetchReferalData();
  }, [filters, selectedCenter]);

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- MRT INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      columnVisibility: {
        updatedAt: false,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "ReferralFees.csv")
        }
      >
        Export CSV
      </button>
    ),
  });
  const clearFilter = () => {
    localStorage.removeItem("selectedCenterId"); // Clear center ID from local storage
    setFilters({
      centerId: "",
    });
    setCenterId(""); // Clear local state for center ID
    setIsClearFilterClicked(true); // Trigger fetch with no filters
  };

  return (
    <div className="container-fluid px-2 my-4 center">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Referral Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          &nbsp;Referral Fees
        </li>
      </ol>
      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Referral Fees</strong>
          </span>
        </div>
        <div className="mb-3 d-flex justify-content-between">
          <div className="individual_fliters d-lg-flex ">
            <div className="form-group mb-0 mb-1">
              <input type="hidden" name="centerId" value={filters.centerId} />
            </div>
          </div>
          {canCreate && <ReferalFeesAdd onSuccess={fetchReferalData} />}
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {canUpdate && (
                <MenuItem>
                  <ReferalFeesEdit
                    id={selectedId}
                    onSuccess={fetchReferalData}
                    onOpen={handleMenuClose}
                  />
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem>
                  <GlobalDelete
                    path={`/deleteReferralFees/${selectedId}`}
                    onDeleteSuccess={fetchReferalData}
                    onOpen={handleMenuClose}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default ReferalFees;
